import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Loader2, Mic, Radio, RefreshCw, Video, X } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { LiveVideoDeviceActionCard, useIntercom, VideoTile } from '../media/LiveVideos';
import { deviceDisplayName } from '../../utils/deviceName';

const normaliseImei = (value) => String(value || '').replace(/^0+/, '');

export const LiveTrackingVideoModal = ({ open, device, onOpenChange, autoStartChannel = null }) => {
  const { api } = useAuth();
  const [streams, setStreams] = useState([]);
  const [startingKey, setStartingKey] = useState(null);
  const [stoppingKeys, setStoppingKeys] = useState(new Set());
  const [deviceStreamTypes, setDeviceStreamTypes] = useState({});
  const [streamDeadlines, setStreamDeadlines] = useState({});
  const [nowTick, setNowTick] = useState(Date.now());
  const [limits, setLimits] = useState({
    max_stream_duration_minutes: 3,
    stream_inactivity_timeout_minutes: 1,
  });
  const intercom = useIntercom();
  const streamsRef = useRef(streams);
  const stoppingRef = useRef(new Set());
  const stoppedTombstonesRef = useRef(new Map());
  const ownedStreamKeysRef = useRef(new Set());
  const lastActivityRef = useRef(Date.now());
  const closingRef = useRef(false);

  useEffect(() => { streamsRef.current = streams; }, [streams]);

  useEffect(() => {
    if (!open) return undefined;
    closingRef.current = false;
    const i = setInterval(() => setNowTick(Date.now()), 1000);
    return () => clearInterval(i);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    api.get('/me/stream-limits')
      .then((r) => {
        const d = r.data || {};
        setLimits({
          max_stream_duration_minutes: d.max_stream_duration_minutes ?? 10,
          stream_inactivity_timeout_minutes: d.stream_inactivity_timeout_minutes ?? 2,
        });
      })
      .catch(() => {});
  }, [api, open]);

  const ownershipKeys = useCallback((deviceId, imei, channel) => {
    const keys = [];
    if (deviceId) keys.push(`${deviceId}|${channel}`);
    if (imei) keys.push(`${normaliseImei(imei)}|${channel}`);
    return keys;
  }, []);

  const rememberLocalStream = useCallback((deviceId, imei, channel) => {
    ownershipKeys(deviceId, imei, channel).forEach((key) => ownedStreamKeysRef.current.add(key));
  }, [ownershipKeys]);

  const forgetLocalStream = useCallback((deviceId, imei, channel) => {
    ownershipKeys(deviceId, imei, channel).forEach((key) => ownedStreamKeysRef.current.delete(key));
  }, [ownershipKeys]);

  const ownsLocalStream = useCallback((deviceId, imei, channel) => (
    ownershipKeys(deviceId, imei, channel).some((key) => ownedStreamKeysRef.current.has(key))
  ), [ownershipKeys]);

  const getDeviceStreamType = useCallback((deviceId) => deviceStreamTypes[deviceId] || 'sub', [deviceStreamTypes]);
  const updateDeviceStreamType = useCallback((deviceId, nextType) => {
    setDeviceStreamTypes((prev) => ({ ...prev, [deviceId]: nextType }));
  }, []);

  const setDeadline = useCallback((deviceId, channel, minutes) => {
    setStreamDeadlines((prev) => ({ ...prev, [`${deviceId}|${channel}`]: Date.now() + minutes * 60_000 }));
  }, []);

  const clearDeadline = useCallback((deviceId, channel) => {
    setStreamDeadlines((prev) => {
      const next = { ...prev };
      delete next[`${deviceId}|${channel}`];
      return next;
    });
  }, []);

  const fetchStreams = useCallback(async () => {
    if (!device?.id && !device?.imei) return;
    try {
      const res = await api.get('/media/native-live/active-sessions');
      const sessions = res.data?.sessions || [];
      const now = Date.now();
      for (const [key, expiresAt] of stoppedTombstonesRef.current.entries()) {
        if (expiresAt <= now) stoppedTombstonesRef.current.delete(key);
      }

      const deviceImei = normaliseImei(device?.imei);
      setStreams((prev) => {
        const enriched = prev.map((s) => {
          const session = sessions.find((x) => normaliseImei(x.imei) === normaliseImei(s.device_imei) && Number(x.channel) === Number(s.channel));
          return session
            ? {
                ...s,
                frames_in: session.frames_in,
                bytes_in: session.bytes_in,
                viewer_count: session.viewer_count,
                stream_type: session.stream_type || s.stream_type,
                _optimistic: false,
              }
            : s;
        });

        sessions.forEach((session) => {
          if (normaliseImei(session.imei) !== deviceImei) return;
          if (session.stream_marked_live === false || session.stream_status === 'stopping') return;
          const channel = Number(session.channel);
          if (!ownsLocalStream(device.id, session.imei, channel)) return;
          if (stoppedTombstonesRef.current.has(`${device.id}|${channel}`)) return;
          if (enriched.some((s) => normaliseImei(s.device_imei) === normaliseImei(session.imei) && Number(s.channel) === channel)) return;
          enriched.push({
            id: `native-${session.imei}-${channel}`,
            device_id: device.id,
            device_imei: normaliseImei(session.imei),
            device_name: session.device_name || deviceDisplayName(device) || `IMEI ${session.imei}`,
            channel,
            camera_id: channel,
            stream_type: session.stream_type || 'sub',
            is_live: true,
            started_at: session.started_at,
            frames_in: session.frames_in,
            bytes_in: session.bytes_in,
            viewer_count: session.viewer_count,
          });
        });
        return enriched;
      });
    } catch (_) {}
  }, [api, device, ownsLocalStream]);

  useEffect(() => {
    if (!open) return undefined;
    fetchStreams();
    const i = setInterval(fetchStreams, 10000);
    return () => clearInterval(i);
  }, [fetchStreams, open]);

  const startStream = useCallback(async (deviceId, channel) => {
    if (!device || deviceId !== device.id) return;
    setStartingKey(`${deviceId}|${channel}`);
    stoppedTombstonesRef.current.delete(`${deviceId}|${channel}`);
    const requestedStreamType = getDeviceStreamType(deviceId);
    try {
      const activeRes = await api.get('/media/native-live/active-sessions');
      const activeSession = (activeRes.data?.sessions || []).find((session) => (
        normaliseImei(session.imei) === normaliseImei(device.imei)
        && Number(session.channel) === Number(channel)
        && session.stream_marked_live !== false
        && session.stream_status !== 'stopping'
      ));

      if (activeSession) {
        rememberLocalStream(deviceId, device.imei || activeSession.imei, channel);
        setStreams((prev) => {
          if (prev.some((p) => p.device_id === deviceId && Number(p.camera_id || p.channel) === Number(channel))) return prev;
          return [...prev, {
            id: `native-${activeSession.imei}-${activeSession.channel}`,
            device_id: deviceId,
            device_imei: normaliseImei(activeSession.imei || device.imei),
            device_name: activeSession.device_name || deviceDisplayName(device) || `IMEI ${activeSession.imei}`,
            channel: activeSession.channel,
            camera_id: activeSession.channel,
            stream_type: activeSession.stream_type || requestedStreamType,
            is_live: true,
            started_at: activeSession.started_at,
            frames_in: activeSession.frames_in,
            bytes_in: activeSession.bytes_in,
            viewer_count: activeSession.viewer_count,
          }];
        });
        if (!device.always_on_streaming && limits.max_stream_duration_minutes > 0) setDeadline(deviceId, channel, limits.max_stream_duration_minutes);
        lastActivityRef.current = Date.now();
        setTimeout(() => fetchStreams().catch(() => {}), 1000);
        return;
      }

      try {
        await api.post('/media/native-capture/stop', { device_id: deviceId, channel, mode: 'live' });
        await new Promise((resolve) => setTimeout(resolve, 800));
      } catch (_) {}

      await api.post('/media/native-capture/start', {
        device_id: deviceId,
        channel,
        mode: 'live',
        stream_type: requestedStreamType,
      });
      rememberLocalStream(deviceId, device.imei, channel);
      setStreams((prev) => {
        if (prev.some((p) => p.device_id === deviceId && Number(p.camera_id || p.channel) === Number(channel))) return prev;
        return [...prev, {
          id: `native-${device.imei || deviceId}-${channel}`,
          device_id: deviceId,
          device_imei: device.imei || '',
          device_name: deviceDisplayName(device) || `Device ${deviceId}`,
          channel,
          camera_id: channel,
          stream_type: requestedStreamType,
          is_live: true,
          _optimistic: true,
          started_at: Date.now() / 1000,
        }];
      });
      if (!device.always_on_streaming && limits.max_stream_duration_minutes > 0) setDeadline(deviceId, channel, limits.max_stream_duration_minutes);
      lastActivityRef.current = Date.now();
      setTimeout(() => fetchStreams().catch(() => {}), 1500);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to start stream');
    } finally {
      setStartingKey(null);
    }
  }, [api, device, fetchStreams, getDeviceStreamType, limits.max_stream_duration_minutes, rememberLocalStream, setDeadline]);

  // Auto-start a requested channel once per open (Vehicle Dashboard one-tap launch).
  const autoStartedRef = useRef(false);
  useEffect(() => {
    if (!open) { autoStartedRef.current = false; return; }
    if (autoStartChannel == null || autoStartedRef.current || !device?.id) return;
    autoStartedRef.current = true;
    startStream(device.id, Number(autoStartChannel));
  }, [open, autoStartChannel, device, startStream]);

  const stopStream = useCallback(async (deviceId, channel, { silent = false } = {}) => {
    const key = `${deviceId}|${channel}`;
    if (stoppingRef.current.has(key)) return 'already_stopping';
    stoppingRef.current.add(key);
    setStoppingKeys((prev) => new Set(prev).add(key));
    stoppedTombstonesRef.current.set(key, Date.now() + 15_000);
    const removedStreams = streamsRef.current.filter((s) => s.device_id === deviceId && Number(s.camera_id || s.channel) === Number(channel));
    const removedStream = removedStreams[0];

    const removeLocalViewer = () => {
      clearDeadline(deviceId, channel);
      removedStreams.forEach((s) => forgetLocalStream(s.device_id, s.device_imei, s.camera_id || s.channel));
      setStreams((prev) => prev.filter((s) => !(s.device_id === deviceId && Number(s.camera_id || s.channel) === Number(channel))));
    };

    try {
      const targetImei = removedStream?.device_imei || device?.imei;
      const activeRes = await api.get('/media/native-live/active-sessions');
      const activeSession = (activeRes.data?.sessions || []).find((session) => (
        normaliseImei(session.imei) === normaliseImei(targetImei)
        && Number(session.channel) === Number(channel)
      ));
      if (activeSession && Number(activeSession.viewer_count || 0) > 1) {
        removeLocalViewer();
        if (!silent) toast.success('Closed this viewer');
        return 'viewer_closed';
      }
      removeLocalViewer();
      await api.post('/media/native-capture/stop', { device_id: deviceId, channel, mode: 'live' });
      if (!silent) toast.success('Stream stopped');
      return 'stream_stopped';
    } catch (e) {
      if (e?.response?.status === 404) {
        removeLocalViewer();
        if (!silent) toast.success('Stream stopped');
        return 'stream_stopped';
      }
      if (!silent) toast.error('Failed to stop stream');
      return 'stop_failed';
    } finally {
      stoppingRef.current.delete(key);
      setStoppingKeys((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
      setTimeout(() => fetchStreams().catch(() => {}), 2500);
    }
  }, [api, clearDeadline, device, fetchStreams, forgetLocalStream]);

  const stopIntercom = useCallback(async (deviceId, { silent = false } = {}) => {
    try { await api.post('/media/native-capture/stop', { device_id: deviceId, channel: 129, mode: 'intercom' }); } catch (_) {}
    intercom.stop();
    if (!silent) toast.success('Intercom stopped');
  }, [api, intercom]);

  const stopModalOwnedStreams = useCallback(async () => {
    const currentStreams = [...streamsRef.current];
    await Promise.all(currentStreams.map((s) => stopStream(s.device_id, s.camera_id || s.channel, { silent: true })));
    if (intercom.activeDeviceId) await stopIntercom(intercom.activeDeviceId, { silent: true });
  }, [intercom.activeDeviceId, stopIntercom, stopStream]);

  const requestClose = useCallback(() => {
    if (closingRef.current) return;
    closingRef.current = true;
    onOpenChange(false);
    stopModalOwnedStreams().catch(() => {});
  }, [onOpenChange, stopModalOwnedStreams]);

  useEffect(() => {
    const inactivityMs = (limits.stream_inactivity_timeout_minutes || 0) * 60_000;
    const idle = Date.now() - lastActivityRef.current > inactivityMs;
    const tabHidden = document.hidden;
    streamsRef.current.forEach((stream) => {
      const deviceId = stream.device_id;
      const channel = Number(stream.camera_id || stream.channel);
      const key = `${deviceId}|${channel}`;
      if (device?.always_on_streaming || stoppingRef.current.has(key)) return;
      if (limits.max_stream_duration_minutes > 0 && !streamDeadlines[key]) setDeadline(deviceId, channel, limits.max_stream_duration_minutes);
      const deadline = streamDeadlines[key];
      if (deadline && nowTick > deadline) {
        clearDeadline(deviceId, channel);
        stopStream(deviceId, channel, { silent: true }).then((result) => {
          if (result === 'viewer_closed') toast.info('This viewer closed (time limit reached)');
          else if (result === 'stream_stopped') toast.info('Stream auto-stopped (time limit reached)');
        });
      } else if (inactivityMs > 0 && tabHidden && idle) {
        clearDeadline(deviceId, channel);
        stopStream(deviceId, channel, { silent: true }).then((result) => {
          if (result === 'viewer_closed') toast.info('This viewer closed (inactive)');
          else if (result === 'stream_stopped') toast.info('Stream auto-stopped (inactive)');
        });
      }
    });
  }, [clearDeadline, device, limits, nowTick, setDeadline, stopStream, streamDeadlines]);

  const extendStream = useCallback((deviceId, channel) => {
    setDeadline(deviceId, channel, limits.max_stream_duration_minutes);
    lastActivityRef.current = Date.now();
    toast.success(`Stream extended ${limits.max_stream_duration_minutes} more minutes`);
  }, [limits.max_stream_duration_minutes, setDeadline]);

  const startIntercom = useCallback(async (targetDevice) => {
    if (intercom.activeDeviceId && intercom.activeDeviceId !== targetDevice.id) {
      toast.warning('Stop the current intercom session before opening another');
      return;
    }
    try {
      await api.post('/media/native-capture/start', {
        device_id: targetDevice.id,
        channel: 129,
        relay_port: 34004,
        mode: 'intercom',
      });
      intercom.startNativeCommand(targetDevice.id);
      const tokenRes = await api.post('/media/native-intercom/token', { device_id: targetDevice.id });
      const intercomToken = tokenRes.data?.intercom_token;
      if (!intercomToken) throw new Error('Intercom token missing');
      const wsBase = process.env.REACT_APP_BACKEND_URL?.replace('https://', 'wss://').replace('http://', 'ws://');
      const wsUrl = `${wsBase}/api/media/native-intercom/${targetDevice.id}/audio?intercom_token=${encodeURIComponent(intercomToken)}`;
      await intercom.startNativeAudio(targetDevice.id, wsUrl);
      toast.success(`Intercom audio active for ${targetDevice.name}`);
    } catch (e) {
      intercom.stop();
      toast.error(e.response?.data?.detail || e.message || 'Intercom failed to start');
    }
  }, [api, intercom]);

  const liveStreams = useMemo(() => streams.filter((s) => s.is_live), [streams]);

  if (!device) return null;

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) requestClose(); else onOpenChange(true); }}>
      <DialogContent
        className="max-h-[92dvh] w-[calc(100vw-1.25rem)] max-w-4xl overflow-hidden p-0 [&>button.absolute]:hidden"
        data-testid="live-tracking-video-modal"
        // Ignore backdrop clicks so an accidental click outside doesn't
        // kill a live stream mid-watch. X button and ESC still close.
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        <audio ref={intercom.downAudioRef} autoPlay playsInline className="hidden" />
        <DialogHeader className="border-b px-4 py-3">
          <div className="flex items-center justify-between gap-3">
            <DialogTitle className="flex min-w-0 items-center gap-2 text-lg">
              <Video className="h-5 w-5 text-red-500" />
              <span className="truncate">{deviceDisplayName(device) || 'Live Video'}</span>
            </DialogTitle>
            <Button variant="ghost" size="icon" className="h-9 w-9 shrink-0" onClick={requestClose} data-testid="live-tracking-video-modal-close-button" aria-label="Close live video modal">
              <X className="h-5 w-5" />
            </Button>
          </div>
        </DialogHeader>
        <div className="max-h-[calc(92dvh-4rem)] overflow-y-auto p-3 sm:p-4" data-testid="live-tracking-video-modal-scroll">
          <div className="grid gap-4 lg:grid-cols-[300px_minmax(0,1fr)]">
            <div className="space-y-3">
              <LiveVideoDeviceActionCard
                device={device}
                activeStreams={liveStreams}
                startingKey={startingKey}
                stoppingKeys={stoppingKeys}
                onPlay={startStream}
                onStop={stopStream}
                intercomActive={intercom.activeDeviceId === device.id}
                onIntercomStart={startIntercom}
                onIntercomStop={(deviceId) => stopIntercom(deviceId)}
                isSelected
                onSelect={() => {}}
                intercomMode={intercom.getMode(device.id)}
                onIntercomModeChange={(nextMode) => intercom.setDeviceMode(device.id, nextMode)}
                isTransmitting={intercom.activeDeviceId === device.id && intercom.isTransmitting}
                onPttPress={() => intercom.setTransmitting(true)}
                onPttRelease={() => intercom.setTransmitting(false)}
                intercomLocked={Boolean(intercom.activeDeviceId && intercom.activeDeviceId !== device.id)}
                streamType={getDeviceStreamType(device.id)}
                onStreamTypeChange={(nextType) => updateDeviceStreamType(device.id, nextType)}
              />
              {intercom.activeDeviceId && (
                <Badge className="w-full justify-center bg-green-500 text-white animate-pulse" data-testid="live-tracking-video-intercom-status-badge">
                  <Mic className="w-3 h-3 mr-1" />
                  {intercom.mode === 'ptt'
                    ? (intercom.isTransmitting ? 'PTT Transmitting' : 'Intercom Listening')
                    : (intercom.isTransmitting ? 'Live Mic Active' : 'Intercom Connecting')}
                </Badge>
              )}
            </div>
            <div className="min-w-0 space-y-3">
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                <Radio className="w-4 h-4 text-red-500" />
                Active Channels <span className="text-foreground">({liveStreams.length})</span>
              </div>
              {liveStreams.length === 0 ? (
                <div className="rounded-lg border border-dashed p-8 text-center text-muted-foreground" data-testid="live-tracking-video-empty-state">
                  <Video className="mx-auto mb-3 h-12 w-12 opacity-50" />
                  <p className="font-medium">No active streams</p>
                  <p className="mt-1 text-sm">Tap a recording channel to begin.</p>
                </div>
              ) : (
                <div className="space-y-3" data-testid="live-tracking-video-player-list">
                  {liveStreams.map((stream) => {
                    const label = device.channel_names?.[String(stream.camera_id)] || `CH${stream.camera_id}`;
                    const alwaysOn = !!device.always_on_streaming;
                    const deadline = streamDeadlines[`${stream.device_id}|${stream.camera_id}`];
                    const timeLeft = (alwaysOn || !deadline) ? null : deadline - nowTick;
                    return (
                      <VideoTile
                        key={stream.id}
                        stream={stream}
                        channelLabel={label}
                        deviceLabel={deviceDisplayName(device)}
                        onStop={stopStream}
                        timeLeftMs={timeLeft}
                        onExtend={() => extendStream(stream.device_id, stream.camera_id)}
                        alwaysOn={alwaysOn}
                        isStopping={stoppingKeys.has(`${stream.device_id}|${stream.camera_id}`)}
                      />
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default LiveTrackingVideoModal;